/**
 * Sales Ranking Chart Component
 * Horizontal bar chart showing sales rep performance rankings
 * Phase 6.2 - Performance Charts
 */

import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { Box, Typography, Skeleton, Select, MenuItem, FormControl, SelectChangeEvent } from '@mui/material';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export interface SalesRepPerformance {
  salesRepId: string;
  name: string;
  revenue: number;
  conversions: number;
  conversionRate: number;
}

export interface SalesRankingChartProps {
  data: SalesRepPerformance[];
  loading?: boolean;
  metric?: 'revenue' | 'conversions' | 'conversionRate';
  onMetricChange?: (metric: 'revenue' | 'conversions' | 'conversionRate') => void;
}

const SalesRankingChart: React.FC<SalesRankingChartProps> = ({
  data,
  loading = false,
  metric = 'revenue',
  onMetricChange
}) => {
  // Sort data by selected metric
  const sortedData = [...data].sort((a, b) => b[metric] - a[metric]);

  // Prepare chart data
  const chartData = {
    labels: sortedData.map(rep => rep.name),
    datasets: [
      {
        label: metric === 'revenue' ? 'Revenue (RM)' :
               metric === 'conversions' ? 'Conversions' :
               'Conversion Rate (%)',
        data: sortedData.map(rep =>
          metric === 'conversionRate' ? rep[metric] : rep[metric]
        ),
        backgroundColor: sortedData.map((_, index) => {
          // Top 3 performers get special colors
          if (index === 0) return 'rgba(46, 125, 50, 0.8)'; // Gold/Green
          if (index === 1) return 'rgba(25, 118, 210, 0.8)'; // Silver/Blue
          if (index === 2) return 'rgba(237, 108, 2, 0.8)'; // Bronze/Orange
          return 'rgba(158, 158, 158, 0.6)'; // Gray for others
        }),
        borderColor: sortedData.map((_, index) => {
          if (index === 0) return 'rgba(46, 125, 50, 1)';
          if (index === 1) return 'rgba(25, 118, 210, 1)';
          if (index === 2) return 'rgba(237, 108, 2, 1)';
          return 'rgba(158, 158, 158, 0.8)';
        }),
        borderWidth: 2,
        borderRadius: 4,
      }
    ]
  };

  // Chart options
  const options: ChartOptions<'bar'> = {
    indexAxis: 'y', // Horizontal bar chart
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      title: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const rep = sortedData[context.dataIndex];
            if (metric === 'revenue') {
              return `Revenue: HKD ${rep.revenue.toLocaleString()}`;
            } else if (metric === 'conversions') {
              return `Conversions: ${rep.conversions}`;
            } else {
              return `Conversion Rate: ${rep.conversionRate.toFixed(1)}%`;
            }
          },
          afterLabel: (context) => {
            const rep = sortedData[context.dataIndex];
            return [
              `Conversions: ${rep.conversions}`,
              `Conversion Rate: ${rep.conversionRate.toFixed(1)}%`,
              `Revenue: HKD ${rep.revenue.toLocaleString()}`
            ];
          }
        },
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 14,
          weight: 'bold'
        },
        bodyFont: {
          size: 13
        }
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: {
          callback: (value) => {
            if (metric === 'revenue') {
              return `HKD ${(value as number / 1000).toFixed(0)}K`;
            } else if (metric === 'conversionRate') {
              return `${value}%`;
            }
            return value;
          }
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        }
      },
      y: {
        grid: {
          display: false
        },
        ticks: {
          font: {
            size: 13,
            weight: '500'
          }
        }
      }
    }
  };

  const handleMetricChange = (event: SelectChangeEvent) => {
    const newMetric = event.target.value as 'revenue' | 'conversions' | 'conversionRate';
    onMetricChange?.(newMetric);
  };

  return (
    <Box>
      {/* Header with metric selector */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6" fontWeight="600">
          Sales Performance Ranking
        </Typography>
        {onMetricChange && (
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <Select
              value={metric}
              onChange={handleMetricChange}
              displayEmpty
            >
              <MenuItem value="revenue">By Revenue</MenuItem>
              <MenuItem value="conversions">By Conversions</MenuItem>
              <MenuItem value="conversionRate">By Conversion Rate</MenuItem>
            </Select>
          </FormControl>
        )}
      </Box>

      {/* Chart */}
      {loading ? (
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 1 }} />
      ) : (
        <Box height={400}>
          <Bar data={chartData} options={options} />
        </Box>
      )}

      {/* Legend for top performers */}
      <Box display="flex" gap={3} mt={2} justifyContent="center" flexWrap="wrap">
        <Box display="flex" alignItems="center" gap={1}>
          <Box width={16} height={16} bgcolor="rgba(46, 125, 50, 0.8)" borderRadius={1} />
          <Typography variant="caption" color="text.secondary">1st Place</Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Box width={16} height={16} bgcolor="rgba(25, 118, 210, 0.8)" borderRadius={1} />
          <Typography variant="caption" color="text.secondary">2nd Place</Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Box width={16} height={16} bgcolor="rgba(237, 108, 2, 0.8)" borderRadius={1} />
          <Typography variant="caption" color="text.secondary">3rd Place</Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default SalesRankingChart;
